// MaturaPolynomialRootsCountClosedTask.js
// Zadanie maturalne zamknięte: liczba rozwiązań równania wielomianowego.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

// zapis (x - a) lub (x + |a|) w LaTeX-ie
function linearFactorLatex(root) {
  if (root === 0) return "x";
  if (root > 0) return `x - ${root}`;
  return `x + ${Math.abs(root)}`;
}

// liczenie różnych wartości (z dokładnością do eps)
function countDistinct(values) {
  const eps = 1e-9;
  const uniq = [];
  values.forEach((v) => {
    const already = uniq.some((u) => Math.abs(u - v) < eps);
    if (!already) uniq.push(v);
  });
  return uniq.length;
}

// lista różnych liczb całkowitych
function uniqueIntegers(values) {
  const eps = 1e-9;
  const uniq = [];
  values.forEach((v) => {
    const nearest = Math.round(v);
    if (!Number.isInteger(nearest) || Math.abs(v - nearest) > eps) return;
    const already = uniq.some((u) => Math.abs(u - nearest) < eps);
    if (!already) uniq.push(nearest);
  });
  return uniq;
}

// poprawna odmiana słowa „rozwiązanie”
function rozwiazaniaWord(n) {
  if (n === 1) return "rozwiązanie";
  if (n >= 2 && n <= 4) return "rozwiązania";
  return "rozwiązań";
}

// tasowanie (używane tylko do faktorów w treści, nie do odpowiedzi)
function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// budowa listy (wartość, krotność) z allRoots
function buildMultiplicityList(allRoots) {
  const eps = 1e-9;
  const list = [];
  allRoots.forEach((v) => {
    const found = list.find((r) => Math.abs(r.value - v) < eps);
    if (found) {
      found.mult += 1;
    } else {
      list.push({ value: v, mult: 1 });
    }
  });
  return list;
}

// ---------------------------------------------------------------------------
// GENERATOR
// ---------------------------------------------------------------------------

function createJune2025OriginalTask() {
  return {
    type: "MaturaPolynomialRootsCountClosedTask",
    data: {
      askKind: "real",
      questionSetText: "w zbiorze liczb rzeczywistych",
      patternType: "squarePlusDiff",
      k: 4,
      a: 1,
      pSquare: null,
      m: 25,
      hasXFactor: false,
      eqLatex: "4(x - 1)^{2}(x^{2} - 25) = 0",
      distinctRealCount: 3,
      integerRoots: [1, 5, -5],
      integerCount: 3,
      allRoots: [1, 1, 5, -5],
      options: [
        { label: "A", value: 2 },
        { label: "B", value: 3 },
        { label: "C", value: 4 },
        { label: "D", value: 5 },
      ],
      correctIndex: 1,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 7,
    },
  };
}

export function generateTask(options = {}) {
  if (options.variant === "original" && options.session === "2025_CZE") {
    return createJune2025OriginalTask();
  }

  const perfectSquares = [1, 4, 9, 16, 25, 36];
  const nonSquares = [2, 3, 5, 6, 7, 8, 10, 11];

  while (true) {
    const askKind = Math.random() < 0.5 ? "real" : "integer";

    // większe prawdopodobieństwo kwadratu sumy/różnicy
    const patternType =
      Math.random() < 2 / 3 ? "squarePlusDiff" : "twoDiffs";

    const k = pick([2, 3, 4, 5]);

    const rootsWithMult = [];
    const factorLatexList = [];

    let a = null;
    let pSquare = null;
    let m = null;

    if (patternType === "squarePlusDiff") {
      // (x - a)^2
      a = pick([-5, -4, -3, -2, -1, 1, 2, 3, 4, 5]);
      rootsWithMult.push(a, a);
      factorLatexList.push(`(${linearFactorLatex(a)})^{2}`);

      // (x^2 - m) – czasem tak, żeby pierwiastek się zdublował
      const a2 = a * a;
      const canOverlap = perfectSquares.includes(a2);
      const makeOverlap = canOverlap && Math.random() < 0.4;

      if (makeOverlap) {
        m = a2;
      } else {
        // TU podkręcamy pierwiastki przy pytaniu o liczby całkowite:
        if (askKind === "integer") {
          const nonSqPool = nonSquares.filter((v) => v !== a2);
          const sqPool = perfectSquares.filter((v) => v !== a2);

          // ~70% szansy, że bierzemy nie-kwadrat → pierwiastki niewymierne
          const useMostlyNonSquares = Math.random() < 0.7 && nonSqPool.length;

          const pool = useMostlyNonSquares
            ? nonSqPool
            : [...nonSqPool, ...sqPool];

          m = pick(pool);
        } else {
          const pool = [...perfectSquares, ...nonSquares].filter(
            (v) => v !== a2
          );
          m = pick(pool);
        }
      }

      const r = Math.sqrt(m);
      rootsWithMult.push(r, -r);
      factorLatexList.push(`(x^{2} - ${m})`);
    } else {
      // "twoDiffs": (x^2 - p^2)(x^2 - m) z m ≠ p^2
      pSquare = pick(perfectSquares);
      const p = Math.round(Math.sqrt(pSquare));
      rootsWithMult.push(p, -p);
      factorLatexList.push(`(x^{2} - ${pSquare})`);

      if (askKind === "integer") {
        const nonSqPool = nonSquares.filter((v) => v !== pSquare);
        const sqPool = perfectSquares.filter((v) => v !== pSquare);
        const useMostlyNonSquares = Math.random() < 0.7 && nonSqPool.length;
        const pool = useMostlyNonSquares
          ? nonSqPool
          : [...nonSqPool, ...sqPool];
        m = pick(pool);
      } else {
        const allSquares = [...perfectSquares, ...nonSquares];
        const pool = allSquares.filter((v) => v !== pSquare);
        m = pick(pool);
      }

      const r2 = Math.sqrt(m);
      rootsWithMult.push(r2, -r2);
      factorLatexList.push(`(x^{2} - ${m})`);
    }

    // opcjonalny czynnik x
    const hasXFactor = Math.random() < 0.5;
    if (hasXFactor) {
      rootsWithMult.push(0);
    }

    const degree = 4 + (hasXFactor ? 1 : 0);
    if (degree > 5) continue;

    const distinctRealCount = countDistinct(rootsWithMult);
    const integerRoots = uniqueIntegers(rootsWithMult);
    const integerCount = integerRoots.length;

    const correctCount =
      askKind === "real" ? distinctRealCount : integerCount;

    // chcemy 2–5 rozwiązań (żeby było sensownie maturalnie)
    if (correctCount < 2 || correctCount > 5) continue;

    // info pomocnicze do wyjaśnień
    const questionSetText =
      askKind === "real"
        ? "w zbiorze liczb rzeczywistych"
        : "w zbiorze liczb całkowitych";

    let prefix = "";
    if (k !== 1) prefix += `${k}`;
    if (hasXFactor) prefix += "x";

    const randomizedFactors = shuffle(factorLatexList);
    const eqLeftLatex = `${prefix}${randomizedFactors.join("")}`;
    const eqLatex = `${eqLeftLatex} = 0`;

    // ---- ODP: cztery KOLEJNE liczby uporządkowane rosnąco ----
    // blok 1–4 lub 2–5, który zawiera correctCount
    const blocks = [];
    if (correctCount >= 1 && correctCount <= 4) {
      blocks.push([1, 2, 3, 4]);
    }
    if (correctCount >= 2 && correctCount <= 5) {
      blocks.push([2, 3, 4, 5]);
    }
    const block = pick(blocks); // np. [1,2,3,4] albo [2,3,4,5]
    const values = [...block].sort((a, b) => a - b); // NATURALNA KOLEJNOŚĆ

    const options = values.map((v, idx) => ({
      label: LETTERS[idx],
      value: v,
    }));

    const correctIndex = options.findIndex(
      (o) => o.value === correctCount
    );

    return {
      type: "MaturaPolynomialRootsCountClosedTask",
      data: {
        askKind,
        questionSetText,
        patternType,
        k,
        a,
        pSquare,
        m,
        hasXFactor,
        eqLatex,
        distinctRealCount,
        integerRoots,
        integerCount,
        allRoots: rootsWithMult,
        options,
        correctIndex,
      },
    };
  }
}

// ---------------------------------------------------------------------------
// RENDER TREŚCI
// ---------------------------------------------------------------------------

export function renderTask({ data }) {
  const { eqLatex, options, askKind } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Równanie <InlineMath math={eqLatex} /> w zbiorze liczb{" "}
        {askKind === "real" ? (
          <u>rzeczywistych</u>
        ) : (
          <u>całkowitych</u>
        )}{" "}
        ma:
      </p>
      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.value}`} />
            <span>{` ${rozwiazaniaWord(opt.value)}`}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// INPUT (ABCD)
// ---------------------------------------------------------------------------

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      value: opt.value,
    });
  };

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ---------------------------------------------------------------------------
// SPRAWDZANIE / ODP. / ODP. UCZNIA
// ---------------------------------------------------------------------------

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];
  return (
    <span>
      <InlineMath math={`${correct.label}.\\; ${correct.value}`} />{" "}
      {rozwiazaniaWord(correct.value)}
    </span>
  );
}


export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, value } = userAnswer;

  return (
    <span>
      <InlineMath math={`${label}.\\; ${value}`} /> {rozwiazaniaWord(value)}
    </span>
  );
}


// ---------------------------------------------------------------------------
// WYJAŚNIENIE
// ---------------------------------------------------------------------------

export function renderExplanation({ data }) {
  const {
    askKind,
    patternType,
    a,
    pSquare,
    m,
    hasXFactor,
    eqLatex,
    distinctRealCount,
    integerRoots,
    integerCount,
    allRoots,
  } = data;

  const setText =
    askKind === "real"
      ? "w zbiorze liczb rzeczywistych"
      : "w zbiorze liczb całkowitych";

  const multList = buildMultiplicityList(allRoots).sort(
    (r1, r2) => r1.value - r2.value
  );
  const integerRootsSorted = [...integerRoots].sort((x, y) => x - y);
  const totalCount =
    askKind === "real" ? distinctRealCount : integerCount;

  const factorEquations = [];

  if (hasXFactor) {
    factorEquations.push({
      latex: "x = 0",
      description: "rozwiązanie z czynnika x",
    });
  }

  if (patternType === "squarePlusDiff") {
    const aLatex = a >= 0 ? `${a}` : `(${a})`;
    factorEquations.push({
      latex: `(x - ${aLatex})^{2} = 0 \\Rightarrow x = ${aLatex}`,
      description: "pierwiastek podwójny z czynnika kwadratowego",
    });

    const r = Math.sqrt(m);
    const rLatex = Number.isInteger(r) ? `${r}` : `\\sqrt{${m}}`;
    factorEquations.push({
      latex: `x^{2} - ${m} = 0 \\Rightarrow x = ${rLatex} \\;\\text{lub}\\; x = -${rLatex}`,
      description: "pierwiastki z różnicy kwadratów",
    });
  } else {
    const p = Math.round(Math.sqrt(pSquare));
    factorEquations.push({
      latex: `x^{2} - ${pSquare} = 0 \\Rightarrow x = ${p} \\;\\text{lub}\\; x = -${p}`,
      description: "pierwiastki z pierwszej różnicy kwadratów",
    });

    const r = Math.sqrt(m);
    const rLatex = Number.isInteger(r) ? `${r}` : `\\sqrt{${m}}`;
    factorEquations.push({
      latex: `x^{2} - ${m} = 0 \\Rightarrow x = ${rLatex} \\;\\text{lub}\\; x = -${rLatex}`,
      description: "pierwiastki z drugiej różnicy kwadratów",
    });
  }

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Zauważamy, że równanie jest zapisane w
        postaci iloczynu nawiasów (czynników):
      </p>
      <BlockMath math={eqLatex} />
      <p>
        Z zerowania iloczynu wynika, że <InlineMath math={"x"} /> jest
        rozwiązaniem wtedy i tylko wtedy, gdy zeruje któryś z czynników.
      </p>

      <p>
        <strong>Krok 2.</strong> Rozwiązujemy kolejno równania wynikające z
        poszczególnych czynników:
      </p>

      <ul style={{ paddingLeft: "1.5rem" }}>
        {factorEquations.map((f, idx) => (
          <li key={idx} style={{ marginBottom: "0.35rem" }}>
            <BlockMath math={f.latex} />
          </li>
        ))}
      </ul>

      <p>
        Z czynników otrzymujemy następujące wartości{" "}
        <InlineMath math={"x"} /> (z krotnościami):
      </p>
      <p>
        {multList.map((r, idx) => {
          let xLatex;
          if (Number.isInteger(r.value)) {
            xLatex = `${r.value}`;
          } else {
            // tu zamiast przybliżenia dziesiętnego zapisujemy w postaci pierwiastka
            if (patternType === "squarePlusDiff" || patternType === "twoDiffs") {
              if (r.value > 0) {
                xLatex = `\\sqrt{${m}}`;
              } else {
                xLatex = `-\\sqrt{${m}}`;
              }
            } else {
              // fallback (teoretycznie nie powinien się zdarzyć)
              xLatex = `${r.value}`;
            }
          }

          const opisKrot =
            r.mult === 1 ? null : <> – pierwiastek <InlineMath math={String(r.mult)} />-krotny</>;
          return (
            <span key={idx} style={{ display: "block" }}>
              <InlineMath math={`x = ${xLatex}`} />
              {opisKrot}
            </span>
          );
        })}
      </p>

      <p>
        Przy liczeniu liczby rozwiązań <strong>liczymy tylko różne</strong>{" "}
        wartości – jeżeli ta sama liczba pojawia się kilka razy (np. z
        czynnika kwadratowego), traktujemy ją jako jedno rozwiązanie.
      </p>

      {askKind === "real" ? (
        <p>
          Po zliczeniu różnych wartości otrzymujemy{" "}
          <InlineMath math={`${distinctRealCount}`} />{" "}
          {rozwiazaniaWord(distinctRealCount)} w zbiorze liczb
          rzeczywistych.
        </p>
      ) : (
        <>
          <p>
            Teraz interesują nas tylko <strong>liczby całkowite</strong>.
            Spośród powyższych wartości wybieramy te, które są całkowite, i
            usuwamy powtórzenia.
          </p>
          <p>
            Ostatecznie otrzymujemy zbiór{" "}
            {integerCount > 0 ? (
              <InlineMath
                math={`\\{${integerRootsSorted.join(",\\; ")}\\}`}
              />
            ) : (
              <InlineMath math={"\\varnothing"} />
            )}
            , który ma{" "}
            <InlineMath math={`${integerCount}`} />{" "}
            {rozwiazaniaWord(integerCount)} w zbiorze liczb całkowitych.
          </p>
        </>
      )}

      <p>
        <strong>Wniosek.</strong> Równanie {setText} ma{" "}
        <InlineMath math={`${totalCount}`} />{" "}
        {rozwiazaniaWord(totalCount)}. To odpowiada poprawnej odpowiedzi
        w zadaniu.
      </p>
    </div>
  );
}
